import React, { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import MuiAlert from '@material-ui/lab/Alert'
import useTheme from '@material-ui/core/styles/useTheme'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useNewsletterSignup } from '../../hooks/useNewsletterSignup'
import FacebookIcon from '@material-ui/icons/Facebook'
import InstagramIcon from '@material-ui/icons/Instagram'
import LinkedInIcon from '@material-ui/icons/LinkedIn'
import TwitterIcon from '@material-ui/icons/Twitter'
import YouTubeIcon from '@material-ui/icons/YouTube'
import Link from '@material-ui/core/Link'
import {
    collectFormErrors,
    removeErrorFromPath,
} from '../../utils/formErrorUtils'
import { newsletterSignupSchema } from '../formValidationSchemas'
import ReCAPTCHA from 'react-google-recaptcha'
import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'
import SimpleBackdrop from '../Loaders/SimpleBackdrop'
import { fullstorySetUserVars } from '../../utils/fullstoryUtils'

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        backgroundColor: theme.palette.secondary.main,
    },
    container: {
        margin: '3rem 1rem',
        borderRadius: 8,
        backgroundColor: 'white',
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            margin: '3rem',
        },
    },
    gridContainer: {
        margin: 'auto',
        [theme.breakpoints.up('sm')]: {
            width: '70%',
            maxWidth: '900px',
        },
    },
    titleText: {
        textAlign: 'center',
        fontSize: 30,
        fontWeight: '700',
        marginTop: '3rem',
    },
    descriptionText: {
        textAlign: 'center',
        margin: '1rem',
        [theme.breakpoints.up('sm')]: {
            margin: '1rem 0',
        },
    },
    gridItem: {
        margin: '0.5rem 1rem',
        [theme.breakpoints.up('sm')]: {
            margin: 0,
        },
    },
    textField: {
        fontSize: '1rem',
    },
    submitButton: {
        fontWeight: 'bold',
        fontSize: '1.1rem',
        textTransform: 'none',
        height: '100%',
    },
    socialIconContainer: {
        display: 'flex',
        justifyContent: 'space-around',
        maxWidth: '400px',
        margin: '1rem auto',
    },
    socialIcon: {
        fontSize: '2.5rem',
        color: theme.palette.secondary.main,
    },
}))

const NewsletterSignupSection = () => {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
        defaultMatches: true,
    })
    const queryClient = useQueryClient()
    const userSessionData = queryClient.getQueryData([
        'loggedIn',
        { isSmb: false },
    ])
    const reCaptchaEnabled = window.React.recaptcha_enabled
    const reCaptchaSiteKey = window.React.recaptcha_site_key
    const reCaptchaRef = useRef()

    const [reCaptchaToken, setReCaptchaToken] = useState(null)
    const [errors, setErrors] = useState([])
    const [email, setEmail] = useState('')
    const [signupErrorMessage, setSignupErrorMessage] = useState('')
    const [alreadySubscribed, setAlreadySubscribed] = useState(false)
    const [signupSuccessMessage, setSignupSuccessMessage] = useState('')

    const {
        mutate: signupForNewsletter,
        isLoading: newsletterSignupIsLoading,
        data: newsletterSignupResponse,
        isSuccess: newsletterSignupIsSuccess,
        isError: newsletterSignupIsError,
        error: newsletterSignupError,
    } = useNewsletterSignup()

    useEffect(() => {
        if (newsletterSignupIsError && reCaptchaRef) {
            reCaptchaRef?.current?.reset()
        }
    }, [newsletterSignupIsError, reCaptchaRef])

    const resetForm = () => {
        setEmail('')
        setErrors([])
    }

    useEffect(() => {
        if (newsletterSignupResponse && newsletterSignupIsSuccess) {
            setSignupSuccessMessage(
                'Successfully signed up for the Gift Card Granny newsletter!'
            )
            resetForm()
        }
    }, [newsletterSignupResponse, newsletterSignupIsSuccess])

    useEffect(() => {
        if (newsletterSignupError) {
            const errorMessages =
                newsletterSignupError?.response?.data?.error ?? []

            if (errorMessages.includes('Member Exists')) {
                setAlreadySubscribed(true)
                setSignupErrorMessage(
                    'You are already signed up for the Gift Card Granny newsletter.'
                )
                resetForm()
            } else if (errorMessages.includes('reCAPTCHA')) {
                setSignupErrorMessage(errorMessages)
            } else {
                setSignupErrorMessage(
                    'Unable to signup for newsletter, please check your info and retry.'
                )
            }
        }
    }, [newsletterSignupError])

    const validateFieldAt = async (path, data) => {
        return await newsletterSignupSchema().validateAt(path, data, {
            abortEarly: false,
        })
    }

    const validateField = (path, value) => {
        validateFieldAt(path, { [path]: value })
            .then(function (response) {
                setErrors(removeErrorFromPath(path, errors))
            })
            .catch(function (e) {
                collectFormErrors(e, errors, setErrors)
            })
    }

    const validateInput = async () => {
        return await newsletterSignupSchema().validate(
            {
                email: email,
            },
            { abortEarly: false }
        )
    }

    const handleNewsletterSignup = (reCapToken) => {
        setReCaptchaToken(reCapToken)
        setSignupErrorMessage('')
        setSignupSuccessMessage('')
        setAlreadySubscribed(false)

        if (!userSessionData?.is_logged_in && email) {
            fullstorySetUserVars({ email })
        }

        const payload = {
            email: email,
            recaptchaToken: reCapToken,
        }
        signupForNewsletter(payload)
    }

    const onNewsletterSignup = () => {
        validateInput()
            .then(() => {
                if (reCaptchaEnabled === '1' && !reCaptchaToken) {
                    reCaptchaRef?.current?.execute()
                } else {
                    handleNewsletterSignup(reCaptchaToken)
                }
            })
            .catch((e) => {
                collectFormErrors(e, errors, setErrors)
            })
    }

    return (
        <Box data-testid="newsletter-signup-section" className={classes.root}>
            <SimpleBackdrop open={newsletterSignupIsLoading} />
            <Box className={classes.container}>
                <Grid
                    container
                    spacing={isMobile ? 0 : 2}
                    className={classes.gridContainer}
                >
                    <Grid item xs={12}>
                        <Typography
                            variant={'h2'}
                            className={classes.titleText}
                        >
                            Sign up for the Gift Card Granny newsletter!
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography className={classes.descriptionText}>
                            Looking for regular savings updates and helpful new
                            blog content? Gift Card Granny's newsletter has
                            both! Sign up for our newsletter to receive the best
                            deals on gift cards for your favorite brands and
                            stay up to date with the newest brands and products
                            Gift Card Granny has to offer. Be sure to select a
                            specific category if you want to be first to hear
                            about those specific gift card brands! Enter your
                            email here to receive updates with the latest
                            discounts on cards for popular stores.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} className={classes.gridItem}>
                        <TextField
                            id="email-address"
                            className={classes.textField}
                            variant="outlined"
                            label="Email address"
                            fullWidth
                            error={has(errors, 'email')}
                            helperText={has(errors, 'email') && errors.email[0]}
                            inputProps={{ maxLength: 256 }}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value.trim())
                                validateField('email', e.target.value)
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} className={classes.gridItem}>
                        <Button
                            data-testid="newsletter-submit-button"
                            variant="contained"
                            color="primary"
                            size="large"
                            className={classes.submitButton}
                            fullWidth
                            aria-label="Newsletter signup"
                            disabled={newsletterSignupIsLoading}
                            onClick={onNewsletterSignup}
                        >
                            Signup
                        </Button>
                    </Grid>
                    <Grid item xs={12} className={classes.gridItem}>
                        {!isEmpty(signupErrorMessage) && (
                            <MuiAlert
                                data-testid="newsletter-signup-alert"
                                severity={alreadySubscribed ? 'info' : 'error'}
                            >
                                {signupErrorMessage}
                            </MuiAlert>
                        )}
                        {!isEmpty(signupSuccessMessage) && (
                            <MuiAlert severity={'success'}>
                                {signupSuccessMessage}
                            </MuiAlert>
                        )}
                    </Grid>
                    <Grid item xs={12} className={classes.socialIconContainer}>
                        <Link
                            href={'https://www.facebook.com/GiftCardGranny'}
                            title="Follow us on Facebook"
                            target={'_blank'}
                            rel={'nofollow noopener'}
                        >
                            <FacebookIcon className={classes.socialIcon} />
                        </Link>
                        <Link
                            href={'https://www.instagram.com/giftcard_granny/'}
                            title="Follow us on Instagram"
                            target={'_blank'}
                            rel={'nofollow noopener'}
                        >
                            <InstagramIcon className={classes.socialIcon} />
                        </Link>
                        <Link
                            href={
                                'https://www.linkedin.com/company/gift-card-granny-llc/'
                            }
                            title="Follow us on LinkedIn"
                            target={'_blank'}
                            rel={'nofollow noopener'}
                        >
                            <LinkedInIcon className={classes.socialIcon} />
                        </Link>
                        <Link
                            href={'https://twitter.com/giftcardgranny'}
                            title="Follow us on Twitter"
                            target={'_blank'}
                            rel={'nofollow noopener'}
                        >
                            <TwitterIcon className={classes.socialIcon} />
                        </Link>
                        <Link
                            href={
                                'https://www.youtube.com/channel/UChf3CH5jTxhr_c-R2rOVEXA/'
                            }
                            title="Follow us on YouTube"
                            target={'_blank'}
                            rel={'nofollow noopener'}
                        >
                            <YouTubeIcon className={classes.socialIcon} />
                        </Link>
                    </Grid>
                </Grid>
            </Box>
            {reCaptchaEnabled === '1' && (
                <ReCAPTCHA
                    ref={reCaptchaRef}
                    sitekey={reCaptchaSiteKey}
                    size={'invisible'}
                    onChange={(reCapToken) => {
                        handleNewsletterSignup(reCapToken)
                    }}
                    onExpired={() => {
                        reCaptchaRef?.current?.reset()
                    }}
                />
            )}
        </Box>
    )
}

export default NewsletterSignupSection
